"use client";

import Image from "next/image";

import { Dispatch, SetStateAction, useState } from "react";
import { CheckedEpisodeDetail, useToast, useUserPoints } from "app/hooks";
import { CreditCardPaymentResponseModel, ProductModel, RentalRequestModel } from "@api/models";
import StripeRegister from "./StripeRegister";
import { paymentTrigger } from "./Payment";
import { ResponseError } from "@api/runtime";
import { CoinIcon, FreeTicketIcon } from "./Icons";
import { GLOBALS } from "config";
import { usePathname } from "next/navigation";
import { CheckoutRequest } from "app/api/checkout/route";
import { deleteCampaignPaymentCookie } from "@lib/analyticLog";
import { PAYMENT_FAILED_ERROR } from "app/constants";
import { calcNewAmountAndTicketWithCheckout } from "@lib/utils";

//カード登録しつつ購入するモード

export function CoinAndRentalWithNewCard({
  checkedEpisodes,
  isSingle,
  seriesId,
  seriesIndexId,
  selectedPointId,
  totalPrice,
  totalBonusTicket,
  points,
  closeFunction,
  setIsCharging,
}: {
  selectedPointId: string | number;
  checkedEpisodes: CheckedEpisodeDetail[];
  isSingle: boolean;
  name: string;
  seriesId: string | null;
  seriesIndexId: number | undefined;
  totalPrice: number;
  totalBonusTicket: number;
  points: ProductModel[];
  closeFunction: () => void;
  setIsCharging: Dispatch<SetStateAction<boolean>>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  //toast
  const { pushToast } = useToast();

  // 保有ポイント
  const { isLoading: isLoadingUserPoints, userPoints } = useUserPoints();

  const pathname = usePathname();

  const selectedEpisodes = checkedEpisodes.filter((ep) => ep !== null);
  const currentPoint = points.find((item) => item.id === selectedPointId);

  // ポイント+チケット購入を考慮してチケット使用枚数の再計算を行う
  const { newTotalPrice, newTotalBonusTicket } = calcNewAmountAndTicketWithCheckout(
    selectedEpisodes,
    userPoints,
    currentPoint,
  );

  const handleClickCoinAndRental = () => {
    if (paymentTrigger.triggerSubmit) {
      setIsLoading(true);
      setIsCharging(true);
      const onErrorHandle = (message?: string) => {
        if (message) {
          pushToast(message, { type: "error" });
        } else {
          pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
            type: "error",
          });
        }
        setIsLoading(false);
        setIsCharging(false);
        closeFunction();
      };
      paymentTrigger
        .triggerSubmit()
        .then((res) => {
          const selected = points.filter((p) => p.id === selectedPointId)[0];
          const selectedTotalPoint = selected.points?.value ?? 0;

          fetch("/api/checkout", {
            method: "POST",
            body: JSON.stringify({
              pointTypeId: Number(selectedPointId),
              cvcToken: res?.token,
              message: `${selectedTotalPoint} コインお得!`,
              redirectUrl: GLOBALS.HOME_URL + pathname,
              articleIds: selectedEpisodes.map((ep) => ep.articleHash),
              articleIndexIds: selectedEpisodes.map((ep) => ep?.articleIndexId),
              ...(seriesId && { seriesId: seriesId }),
              ...(seriesIndexId && { seriesIndexId: seriesIndexId }),
              amount: newTotalPrice,
              ticketCount: newTotalBonusTicket,
            } as CheckoutRequest),
          })
            .then(async (res) => {
              if (res.status !== 200) {
                // 400エラーの場合はBEからエラー理由のメッセージが返ってくるのでそれをトーストに表示
                if (res.status === 400 && res.body) {
                  const json = await res.json();
                  if (json.message) {
                    throw new Error(json.message, {
                      cause: PAYMENT_FAILED_ERROR,
                    });
                  }
                }
                throw new Error();
              }
              const data = (await res.json()) as CreditCardPaymentResponseModel;
              if (data["_3dSecureRedirectUrl"]) {
                // 3DSリダイレクト
                window.location.href = data["_3dSecureRedirectUrl"];
              } else {
                // 3DSリダイレクトがなければチャージ処理まで行われているため状態取得は必要なし
                // TODO: 表示するメッセージ内容検討(ボーナス含めるか等)
                pushToast(`${selected.points?.value}コインを購入しました。`);
                // そのままレンタル処理
                fetch("/api/checkout/rental", {
                  method: "POST",
                  body: JSON.stringify({
                    articleIds: selectedEpisodes.map((ep) => ep.articleHash),
                    ...(!isSingle && { seriesId: seriesId }),
                    amount: newTotalPrice,
                    ticketCount: newTotalBonusTicket,
                  } as RentalRequestModel),
                })
                  .then(async (res) => {
                    if (res.status !== 200) {
                      throw new ResponseError(res);
                    }
                    pushToast(`エピソードを${GLOBALS.IS_RENTAL ? "レンタル" : "購入"}しました。`);
                    selectedEpisodes.map((ep) => {
                      deleteCampaignPaymentCookie("article", ep?.articleIndexId);
                    });
                    deleteCampaignPaymentCookie("series", seriesIndexId);
                    setTimeout(() => {
                      closeFunction();
                      setIsLoading(false);
                      window.location.reload();
                    }, 1000);
                  })
                  .catch(() => {
                    onErrorHandle();
                  });
              }
            })
            .catch((e) => {
              if (e instanceof Error && e.cause === PAYMENT_FAILED_ERROR) {
                onErrorHandle(e.message);
              } else {
                onErrorHandle();
              }
            });
        })
        .catch(() => {
          //カードのエラーはカード登録側で出力するのでここではキャンセルのみ
          handleCancel();
        });
    }
  };
  const handleCancel = () => {
    setIsCharging(false);
    closeFunction();
  };

  return (
    <>
      <div className="dialog-pay-h">
        <div className="dialog-pay-h-main">
          <span className="dialog-pay-h-title">
            <CoinIcon size={18} className="dialog-pay-icon" />
            {currentPoint?.points?.value.toLocaleString()}
            {(currentPoint?.points?.bonusValue || 0) > 0 && (
              <>
                &nbsp;+&nbsp;
                <FreeTicketIcon size={22} className="dialog-pay-icon" />
                {currentPoint?.points?.bonusValue.toLocaleString()}枚
              </>
            )}
          </span>
          <span className="dialog-pay-h-price mode-charge">
            ¥{currentPoint?.price.toLocaleString()}
          </span>
        </div>
      </div>
      {/* TODO: 購入後、優先してチケットが使われるエピソードの特定 */}
      <p className="dialog-pay-after">
        チャージ後{" "}
        {newTotalPrice > 0 && (
          <>
            <Image
              src="/images/icons/coin.svg"
              width={18}
              height={18}
              alt="コイン"
              className="dialog-pay-icon"
            />
            {newTotalPrice}
          </>
        )}
        {newTotalBonusTicket > 0 && (
          <>
            <FreeTicketIcon size={18} className="dialog-pay-icon" />
            {newTotalBonusTicket}枚
          </>
        )}
        を消費します
      </p>
      <div className="dialog-pay-main">
        <StripeRegister inputOnly={true} />
        <div className="dialog-btns mode-no-inline-pad">
          <button
            type="button"
            className={"dialog-btn mode-pay" + (isLoading ? " mode-loading" : "")}
            onClick={() => handleClickCoinAndRental()}
            disabled={isLoading}
          >
            チャージして読む
          </button>

          <button
            type="button"
            className="dialog-btn mode-moderate"
            onClick={handleCancel}
            disabled={isLoading}
          >
            キャンセルする
          </button>
        </div>
      </div>
    </>
  );
}
